// src/pages/SchedulePage.jsx
import React, { useMemo, useState } from "react";
import "../styles/schedule.css";

/* 날짜 유틸 */
const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const fmtDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => addDays(new Date(d), -new Date(d).getDay());
const endOfWeek = (d) => addDays(startOfWeek(d), 6);

/* 로컬 스토리지 (조회만) */
const LS_KEY = "scheduleEvents";
const loadEvents = () => {
  try { const raw = localStorage.getItem(LS_KEY); return raw ? JSON.parse(raw) : []; }
  catch { return []; }
};
/* 데모 시드: 비어있을 때만 1회 주입 */
const seedIfEmpty = () => {
  if (loadEvents().length) return;
  const base = new Date();
  const nextSun = addDays(base, (7 - base.getDay()) % 7);
  const mk = (offsetDay, time, title) => ({
    id: crypto.randomUUID(),
    date: fmtDate(addDays(nextSun, offsetDay)),
    time, title, place: "", tags: []
  });
  const demo = [
    mk(0, "오전 11:00", "프리 • 홍길동"),
    mk(0, "오후 02:00", "스쿠버 • 파디"),
    mk(0, "오후 05:00", "스쿠버 • 파디"),
    mk(2, "오후 03:00", "프리 • 홍길동"),
    mk(2, "오후 07:00", "스쿠버 • 홍길동"),
    mk(2, "오후 08:00", "스쿠버 • 송도"),
    mk(2, "오후 10:00", "프리 • 파디"),
    mk(2, "오후 11:00", "스쿠버 • 수원"),
  ];
  localStorage.setItem(LS_KEY, JSON.stringify(demo));
};

export default function SchedulePage() {
  const [view, setView] = useState("month"); // "month" | "list"
  const [cursor, setCursor] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [events] = useState(() => { seedIfEmpty(); return loadEvents(); });

  const gotoPrev = () => setCursor((c) =>
    view === "month"
      ? new Date(c.getFullYear(), c.getMonth() - 1, 1)
      : addDays(c, -7)
  );
  const gotoNext = () => setCursor((c) =>
    view === "month"
      ? new Date(c.getFullYear(), c.getMonth() + 1, 1)
      : addDays(c, +7)
  );
  const gotoToday = () =>
    setCursor(
      view === "month"
        ? new Date(new Date().getFullYear(), new Date().getMonth(), 1)
        : startOfWeek(new Date())
    );

  const periodTitle = useMemo(() => {
    if (view === "month")
      return `${cursor.getFullYear()}년 ${cursor.getMonth() + 1}월`;
    const s = startOfWeek(cursor), e = endOfWeek(cursor);
    return `${s.getFullYear()}년 ${s.getMonth() + 1}월 ${s.getDate()}일 – ${e.getFullYear()}년 ${e.getMonth() + 1}월 ${e.getDate()}일`;
  }, [view, cursor]);

  // 월 보기를 위한 셀(6주)
  const monthCells = useMemo(() => {
    if (view !== "month") return [];
    const y = cursor.getFullYear(), m = cursor.getMonth();
    const first = new Date(y, m, 1);
    const firstCell = startOfWeek(first);
    return Array.from({ length: 42 }, (_, i) => addDays(firstCell, i));
  }, [view, cursor]);

  // 범위 내 이벤트 맵
  const eventsByDate = useMemo(() => {
    const map = new Map();
    const push = (ev) => {
      const arr = map.get(ev.date) || [];
      arr.push(ev);
      map.set(ev.date, arr);
    };
    if (view === "month") {
      const start = monthCells[0], end = monthCells[41];
      events.forEach((ev) => { const d = new Date(ev.date); if (d >= start && d <= end) push(ev); });
    } else {
      const s = startOfWeek(cursor), e = endOfWeek(cursor);
      events.forEach((ev) => { const d = new Date(ev.date); if (d >= s && d <= e) push(ev); });
    }
    for (const [k, arr] of map.entries()) arr.sort((a, b) => (a.time || "").localeCompare(b.time || "", "ko"));
    return map;
  }, [events, view, cursor, monthCells]);

  return (
    <div className="schedule-page">
      <section className="sched-section">
        {/* 히어로 아래 본문 타이틀/설명 */}
        <header className="sched-header">
          <h2>강습 일정</h2>
          <p>강습 일정을 한눈에 확인하고 원하는 날짜에 참여할 수 있습니다.</p>
        </header>

        <div className="sched-card">
          {/* 툴바 */}
          <div className="sched-toolbar">
            <div className="left">
              <button className="btn" onClick={gotoPrev} aria-label="이전">
                &lt;
              </button>
              <button className="btn" onClick={gotoToday}>오늘</button>
              <button className="btn" onClick={gotoNext} aria-label="다음">
                &gt;
              </button>
            </div>
            <div className="center">
              <div className="sched-title">{periodTitle}</div>
            </div>
            <div className="right">
              <button
                className={`btn ${view === "month" ? "solid" : ""}`}
                onClick={() => setView("month")}
              >
                월
              </button>
              <button
                className={`btn ${view === "list" ? "solid" : ""}`}
                onClick={() => { setView("list"); setCursor(startOfWeek(new Date())); }}
              >
                일정목록
              </button>
            </div>
          </div>

          {/* 본문 */}
          {view === "month" ? (
            <MonthView
              monthCells={monthCells}
              cursor={cursor}
              eventsByDate={eventsByDate}
              onDayClick={(d) => { setView("list"); setCursor(startOfWeek(d)); }}
            />
          ) : (
            <ListView cursor={cursor} eventsByDate={eventsByDate} />
          )}
        </div>
      </section>
    </div>
  );
}

/* ===== Month View: 모든 일정 표시, 셀은 내용에 맞춰 늘어남 ===== */
function MonthView({ monthCells, cursor, eventsByDate, onDayClick }) {
  const labels = ["일", "월", "화", "수", "목", "금", "토"];
  const month = cursor.getMonth();
  return (
    <div className="month">
      <div className="month-header">
        {labels.map((s) => (
          <div key={s} className="dow">{s}</div>
        ))}
      </div>

      <div className="month-grid">
        {monthCells.map((d, i) => {
          const out = d.getMonth() !== month;
          const today = sameDay(d, new Date());
          const items = eventsByDate.get(fmtDate(d)) || [];
          return (
            <div
              key={i}
              className={`cell ${out ? "out" : ""} ${today ? "today" : ""}`}
              onClick={() => onDayClick(d)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && onDayClick(d)}
            >
              <div className="date">{d.getDate()}</div>

              {items.length > 0 && (
                <ul className="bullets">
                  {items.map((ev) => (
                    <li key={ev.id}>
                      <span className="dot" />
                      <span className="txt">
                        {(ev.time || "").replace(/^오전|^오후/, (m) => (m === "오전" ? "오전 " : "오후 "))} {ev.title}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ===== Week List View ===== */
function ListView({ cursor, eventsByDate }) {
  const s = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(s, i));
  const w = ["일", "월", "화", "수", "목", "금", "토"];
  return (
    <div className="list">
      {days.map((d) => {
        const ds = fmtDate(d);
        const items = eventsByDate.get(ds) || [];
        return (
          <div key={ds} className="list-day">
            <div className="list-day-head">
              <div className="weekday">{w[d.getDay()]}</div>
              <div className="date">
                {`${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`}
              </div>
            </div>

            {items.length === 0 ? (
              <div className="list-empty">일정 없음</div>
            ) : (
              <ul className="list-items">
                {items.map((ev) => (
                  <li key={ev.id}>
                    <span className="dot" />
                    <span className="time">{ev.time || ""}</span>
                    <span className="title">{ev.title}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
